import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormSchemaV2 } from '../../../lib/agentShared/formSchema';
import {
  EMPTY_COMMAND_STACK,
  dispatchReportStudioCommand,
  redoReportStudio,
  undoReportStudio,
} from './reportStudioCommands';
import type { CommandStack, ReportStudioCommand } from './reportStudioCommands';
import { defaultReportPresentation, migrateReportPresentation, pruneOrphanedSections } from './reportPresentationSchema';
import type { ReportPresentation } from './reportPresentationSchema';

export interface UseReportStudioArgs {
  schema: FormSchemaV2;
  /** Called with the next `FormSchemaV2` whenever a command changes the
   * `report` slice — the SAME `handleChange` `TemplateEditorPage` already
   * passes to `EditorCanvas`/the rest of the Build tab, so autosave,
   * acknowledgement and the crash-recovery snapshot keep working exactly as
   * they did before this story (this hook adds a typed layer in FRONT of
   * that pipeline, it does not replace it). */
  onSchemaChange: (next: FormSchemaV2) => void;
}

export interface UseReportStudioResult {
  presentation: ReportPresentation;
  dispatch: (command: ReportStudioCommand) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Report Studio's own typed-command/undo domain (story 4-113) — see
 * `reportStudioCommands.ts`'s header comment for the full architecture.
 *
 * Deliberately a SEPARATE stack from `TemplateEditorPage`'s own whole-schema
 * snapshot undo (`pastRef`/`futureRef`, used for the Build/Preview tabs,
 * untouched by this story): `ReportPresentation` lives outside
 * `FormSchemaV2` entirely, so a snapshot of the schema alone could never
 * cover it, and a command's own computed inverse (see
 * `applyReportStudioCommand`) is a more precise undo than "restore the last
 * full schema snapshot" once `presentation` needs undoing too. The page
 * still exposes ONE pair of Undo/Redo buttons in its header — it delegates
 * to THIS hook's `undo`/`redo` while the Report tab is active, and to its
 * own schema-snapshot undo otherwise (see `TemplateEditorPage.tsx`).
 *
 * `presentation` is intentionally NOT persisted anywhere yet (the judgment
 * call recorded in `reportPresentationSchema.ts`'s header comment) — it
 * resets to defaults on mount/remount, same lifetime as any other purely
 * client-side editor-session state in this codebase today. The first
 * tool-group story that needs a real per-section/header/footer/block
 * property to survive a reload decides where it is saved.
 */
export function useReportStudio({ schema, onSchemaChange }: UseReportStudioArgs): UseReportStudioResult {
  const [presentation, setPresentation] = useState<ReportPresentation>(() => migrateReportPresentation(undefined));
  // Mirrors `presentation` synchronously (see the effect below) so
  // dispatch/undo/redo below can read the LATEST value without a stale
  // closure or a functional `setState` updater — React 18 Strict Mode
  // double-invokes updater functions to catch impure ones, and these three
  // callbacks have real side effects (`onSchemaChange`, mutating
  // `stackRef`), so they must not live inside a `setState(prev => ...)`
  // updater at all.
  const presentationRef = useRef(presentation);
  const stackRef = useRef<CommandStack>(EMPTY_COMMAND_STACK);
  const [historyTick, setHistoryTick] = useState(0);

  useEffect(() => {
    presentationRef.current = presentation;
  }, [presentation]);

  const liveSectionIds = useMemo(() => new Set(schema.sections.map((section) => section.id)), [schema.sections]);

  // Edge cases: "a referenced section/field was deleted from the template"
  // / "a restored template version has different section ids" — pruned on
  // every schema change, not just once at mount, so a mid-session delete or
  // a conflict-reload's replacement schema (`TemplateEditorPage.handleReload`)
  // cannot leave presentation config pointing at a ghost section id.
  useEffect(() => {
    setPresentation((current) => pruneOrphanedSections(current, liveSectionIds));
  }, [liveSectionIds]);

  const dispatch = useCallback((command: ReportStudioCommand) => {
    const { state, stack } = dispatchReportStudioCommand(
      { schema, presentation: presentationRef.current },
      stackRef.current,
      command,
    );
    stackRef.current = stack;
    presentationRef.current = state.presentation;
    setPresentation(state.presentation);
    onSchemaChange(state.schema);
    setHistoryTick((tick) => tick + 1);
  }, [schema, onSchemaChange]);

  const undo = useCallback(() => {
    const { state, stack } = undoReportStudio({ schema, presentation: presentationRef.current }, stackRef.current);
    stackRef.current = stack;
    presentationRef.current = state.presentation;
    setPresentation(state.presentation);
    onSchemaChange(state.schema);
    setHistoryTick((tick) => tick + 1);
  }, [schema, onSchemaChange]);

  const redo = useCallback(() => {
    const { state, stack } = redoReportStudio({ schema, presentation: presentationRef.current }, stackRef.current);
    stackRef.current = stack;
    presentationRef.current = state.presentation;
    setPresentation(state.presentation);
    onSchemaChange(state.schema);
    setHistoryTick((tick) => tick + 1);
  }, [schema, onSchemaChange]);

  void historyTick; // re-render trigger for canUndo/canRedo below

  return {
    presentation,
    dispatch,
    undo,
    redo,
    canUndo: stackRef.current.past.length > 0,
    canRedo: stackRef.current.future.length > 0,
  };
}

export { defaultReportPresentation };
